import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Notification } from 'components'
import { Variant as NotificationVariant } from 'components/Notification'
import { useLocation, Redirect, useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { Spinner } from 'lp-components'
import { useParticipantsSummaryTaxableAccounts } from 'utils'
import * as actions from '../actions'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { ParticipantsSummaryTable, FlowActions } from '../components'
import { Path } from 'config/portal'
import { AgreementsMapping, ProductTypes } from 'config/products'
import getUploadParticipantAccordionItems from '../../products/components/getUploadParticipantAccordionItems'
import { FlatfileProvider } from '@flatfile/react'
import FlatfileUpload from '../../../components/FlatfileUpload'
import { RecordTypes } from '../../../flatfileConfig'
import { ReasonForDiscard } from 'config/automatic-rollovers'
import ParticipantUploadInstructionsSection from '../../products/components/ParticipantUploadInstructionsSection'
import {
  getParticipantFileUploadExpandedItems,
  setParticipantFileUploadExpandedItems,
} from 'local-storage'
import ParticipantUploadLegaleseSection from '../../products/components/ParticipantUploadLegaleseSection'
import { usePlanMatchesEffect } from '../../products/hooks/usePlanMatchesEffect'

const propTypes = {
  legaleseConfirmationForm: PropTypes.object,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  fetchCompletedServicesAgreements: PropTypes.func.isRequired,
  completedServicesAgreements: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  setParticipantFileName: PropTypes.func.isRequired,
  setLegaleseConfirmationForm: PropTypes.func.isRequired,
  setParticipantsForSubmission: PropTypes.func.isRequired,
  resetDiscardedParticipants: PropTypes.func.isRequired,
  participantFileName: PropTypes.string,
  fetchPlanMatches: PropTypes.func.isRequired,
}

const defaultProps = {
  legaleseConfirmationForm: {},
  completedServicesAgreements: null,
  participantFileName: null,
}
const accordionItems = getUploadParticipantAccordionItems()

function UploadParticipantFileView({
  legaleseConfirmationForm,
  participantsForSubmission,
  setParticipantsForSubmission,
  resetDiscardedParticipants,
  participantFileName,
  setParticipantFileName,
  fetchCompletedServicesAgreements,
  completedServicesAgreements,
  setLegaleseConfirmationForm,
  fetchPlanMatches,
}) {
  const history = useHistory()
  const location = useLocation()
  const [errorMessage, setErrorMessage] = useState(null)
  const [error, setError] = useState(null)
  const formRef = useRef()
  const { data: participantsSummaryData } =
    useParticipantsSummaryTaxableAccounts(participantsForSubmission)
  const [expandedItems, setExpandedItems] = useState(
    getParticipantFileUploadExpandedItems() || []
  )
  const [showAccordion, setShowAccordion] = useState(false)
  const TAXABLE_ACCOUNTS_TEMPLATE_URL =
    process.env.REACT_APP_TAXABLE_ACCOUNTS_TEMPLATE_URL

  useEffect(() => {
    async function getCompletedServicesAgreements() {
      try {
        await fetchCompletedServicesAgreements(
          AgreementsMapping.TAXABLE_ACCOUNTS_PLAN_MATCHING
        )
      } catch {
        setError(true)
      }
    }
    getCompletedServicesAgreements()
  }, [])

  useEffect(() => {
    if (!isEmpty(participantsForSubmission)) {
      setShowAccordion(false)
    }
  }, [participantsForSubmission])

  usePlanMatchesEffect({
    participantsForSubmission,
    fetchPlanMatches,
    productType: ProductTypes.TAXABLE_ACCOUNTS,
    setError,
  })
  const handleToggleAccordion = () => {
    setShowAccordion(!showAccordion)
  }
  const handleSubmit = async (values) => {
    // if, for instance, flatfile error exists, do nothing
    if (errorMessage) return
    if (isEmpty(participantsForSubmission)) {
      setErrorMessage('Upload a valid file to continue')
      return
    }
    setParticipantFileUploadExpandedItems(expandedItems) // set expanded items in localStorage
    setLegaleseConfirmationForm(values)
    history.push({
      pathname: '/taxable-accounts/plan-mapping',
      state: {
        search: location.state?.search,
      },
    })
  }

  if (!completedServicesAgreements && !error) {
    return (
      <div className="upload-participant-file-view-container">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <Redirect
        push
        to={{
          pathname: '/error',
          state: {
            ignorePrompt: true, // No need to warn the user about leaving the flow
            search: location.state?.search,
          },
        }}
      />
    )
  }

  // Prevent the user from accessing the flow if the user 1) has no completed services agreements
  // or 2) did not go through the start-new-rollover modal from the table view first

  if (!completedServicesAgreements.length) {
    return (
      <Redirect
        push
        to={{
          pathname: Path.TAXABLE_ACCOUNTS_SERVICES_AGREEMENTS,
          state: {
            ignorePrompt: true,
            search: location.state?.search,
          },
        }}
      />
    )
  }

  return (
    <div className="card full-height upload-participant-file-view-container">
      <div className="form-masthead">
        <h2>Start by adding your participants</h2>
      </div>
      <div className="form-section form-section--new-spreadsheet">
        <div className="form-card-content upload-container">
          <div className="form-header">
            <p>
              You can add participants by manually completing our online
              participant file or by uploading the participant data spreadsheet.
            </p>
            <Notification type={NotificationVariant.WARNING}>
              <p className="text-title--small">
                Make sure you have a completed services agreement on file for
                your participant’s corresponding plan before continuing.
              </p>
            </Notification>
          </div>
          <FlatfileProvider
            publishableKey={process.env.REACT_APP_FLATFILE_PK}
            config={{
              preload: true,
              resetOnClose: true,
            }}
          >
            <FlatfileUpload
              recordType={RecordTypes.TAXABLE_ACCOUNTS_PARTICIPANT}
              buttonContent="Add participant information"
              setFileName={setParticipantFileName}
              fileName={participantFileName}
              handleValidData={(validData) => {
                setParticipantsForSubmission(validData)
                setErrorMessage('')
              }}
              handleInvalidData={(discardedData) => {
                const discardedParticipantsWithReason = discardedData.map(
                  (participant) => ({
                    ...participant,
                    reasonForDiscard: ReasonForDiscard.INVALID_DATA,
                  })
                )
                resetDiscardedParticipants(discardedParticipantsWithReason)
              }}
              participantsForSubmission={participantsForSubmission}
            />
          </FlatfileProvider>
          {!!errorMessage && (
            <span className="error-message">{errorMessage}</span>
          )}
          {!isEmpty(participantsSummaryData) && (
            <ParticipantsSummaryTable
              data={participantsSummaryData}
              title={<h4>Participant file summary</h4>}
            />
          )}
        </div>
      </div>
      <ParticipantUploadInstructionsSection
        showAccordion={showAccordion}
        handleToggleAccordion={handleToggleAccordion}
        accordionItems={accordionItems}
        setExpandedItems={setExpandedItems}
        link={TAXABLE_ACCOUNTS_TEMPLATE_URL}
      />
      <ParticipantUploadLegaleseSection
        formRef={formRef}
        legaleseConfirmationForm={legaleseConfirmationForm}
        handleSubmit={handleSubmit}
        taxable={true}
      />
      <FlowActions
        preventBack
        handleSubmit={() => formRef.current.handleSubmit()}
      />
    </div>
  )
}

UploadParticipantFileView.propTypes = propTypes

UploadParticipantFileView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    participantsForSubmission: selectors.participantsForSubmission(state),
    completedServicesAgreements: selectors.completedServicesAgreements(state),
    legaleseConfirmationForm: selectors.legaleseConfirmationForm(state),
    participantFileName: selectors.participantFileName(state),
  }
}

const mapDispatchToProps = {
  setParticipantsForSubmission: actions.setParticipantsForSubmission,
  fetchCompletedServicesAgreements: apiActions.fetchCompletedServicesAgreements,
  setParticipantFileName: actions.setParticipantFileName,
  setLegaleseConfirmationForm: actions.setLegaleseConfirmationForm,
  resetDiscardedParticipants: actions.resetDiscardedParticipants,
  fetchPlanMatches: apiActions.fetchPlanMatches,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  UploadParticipantFileView
)
